import {
  Avatar,
  Button,
  Col,
  Modal,
  Radio,
  Row,
  Spin,
  Table,
  Tooltip,
} from "antd";
import { ColumnsType } from "antd/es/table";
import dayjs from "dayjs";
import React, { useState } from "react";

import { DiscordMembers } from "../../../types/discord_members";
// eslint-disable-next-line import/extensions
import ExportMenu from "./ExportMenu";
import useRetentionRpc from "./useRetentionRpc";

const columns: ColumnsType<DiscordMembers> = [
  {
    key: "username",
    title: "Username",
    dataIndex: "username",
    render: (text: string, record: DiscordMembers) => (
      <div style={{ display: "flex", justifyContent: "flex-start" }}>
        <div>
          <Avatar
            src={
              record.avatar
                ? `https://cdn.discordapp.com/avatars/${record.discord_id}/${record.avatar}.png`
                : "/avatar_slack.png"
            }
            style={{ marginRight: 15 }}
          />
        </div>
        <div style={{ maxWidth: "200px" }}>
          <div style={{ color: "#111213" }}>{text}</div>
        </div>
      </div>
    ),
    width: "100%",
    sorter: (a: DiscordMembers, b: DiscordMembers) =>
      a.username.localeCompare(b.username),
  },
];

type RetentionProps = {
  interval: string;
  roleId?: string | null; //If we want to filter by role
};

const RetentionChart = ({ interval, roleId }: RetentionProps) => {
  const [isMemberModalVisible, setIsMemberModalVisible] =
    useState<boolean>(false);
  const [memberType, setMemberType] = useState<string>("active");
  const [isNewMemberModalVisible, setIsNewMemberModalVisible] =
    useState<boolean>(false);
  const [cohortDates, setCohortDates] = useState({
    currentMonth: 0,
    cohortDate: "",
  });

  const guildId: string = localStorage.getItem("guild-id") || "";
  const retentionData = useRetentionRpc(guildId, interval, roleId);
  const { data } = retentionData;
  const handleMemberType = (e: any) => {
    setMemberType(e.target.value);
  };

  const handleCohortDates = (currentMonth: number, cohortDate: string) => {
    setCohortDates({
      currentMonth,
      cohortDate: dayjs(cohortDate).format("MMMM"),
    });
  };
  return (
    <div>
      <Spin spinning={retentionData.isLoading}>
        {data ? (
          <div>
            {data.map((item: any, mainIndex: number) => (
              <Row className="m-1">
                {data[0].map((_element: string | number, index: number) => {
                  if (index === 0 || index === 1) {
                    // check if first row
                    if (mainIndex === 0) {
                      return (
                        <Col span={2} className="font-semibold">
                          {item[index]}
                        </Col>
                      );
                    }
                    // otherwise return regular. Check if its first item to format with date
                    return (
                      <Col span={2}>
                        {index === 0 ? (
                          dayjs(item[index]).format("MMM YY")
                        ) : index === 1 ? ( // Members joined (number)
                          <Button
                            type="link"
                            onClick={() => {
                              retentionData.getNewCohortMembersOnly(
                                dayjs(item[0]).format("MM/DD/YYYY"),
                                roleId
                              );
                              handleCohortDates(index - 2, item[0]);
                              setIsNewMemberModalVisible(true);
                            }}
                          >
                            {item[index]}
                          </Button>
                        ) : (
                          <Button
                            type="link"
                            onClick={() => {
                              retentionData.getAllCohortMembers(
                                dayjs(item[0]).format("MM/DD/YYYY"),
                                index - 1,
                                roleId
                              );
                              setIsMemberModalVisible(true);
                            }}
                          >
                            {item[index][0]}
                          </Button>
                        )}
                      </Col>
                    );
                  }
                  // if the array is shorter than the first array item, show blank
                  if (index + 1 > item.length) {
                    return (
                      <Col
                        span={Math.floor(19 / (data[0].length - 2))}
                        className="bg-white"
                      />
                    );
                  }
                  // lastly check if its the rest of the header
                  if (mainIndex === 0) {
                    return (
                      <Col
                        span={Math.floor(19 / (data[0].length - 2))}
                        className="font-semibold text-center "
                      >
                        {item[index]}
                      </Col>
                    );
                  }
                  // otherwise return the item + opacity
                  // opacity is divided by 50 because most communities wil have a low retention so the colors need to be adjusted
                  return (
                    <Col
                      span={Math.floor(19 / (data[0].length - 2))}
                      className="mr-1 place-items-center text-center rounded-md"
                      style={{
                        backgroundColor: `rgba(233, 81, 19, ${
                          Math.floor(item[index].retention_rate) / 30
                        })`,
                      }}
                    >
                      <Tooltip title="Click to view members">
                        <button
                          style={{
                            color: "#111213",
                            cursor: "pointer",
                            width: "100%",
                            height: "100%",
                          }}
                          role="button"
                          tabIndex={0}
                          onClick={() => {
                            window.analytics.track(
                              "Clicked on member retention view members button",
                              {
                                platform: "discord",
                              }
                            );
                            retentionData.getAllCohortMembers(
                              dayjs(item[0]).format("MM/DD/YYYY"),
                              item[index].retention_month,
                              roleId
                            );
                            handleCohortDates(index - 2, item[0]);
                            setIsMemberModalVisible(true);
                          }}
                        >
                          {Math.round(item[index].retention_rate * 10) / 10}%
                        </button>
                      </Tooltip>
                    </Col>
                  );
                })}
              </Row>
            ))}
          </div>
        ) : (
          <p>No data. Please try again. </p>
        )}
      </Spin>
      <Modal
        width={600}
        title="Active and Inactive cohort members"
        style={{ top: 40, right: 10, borderRadius: 20 }}
        visible={isMemberModalVisible}
        onCancel={() => setIsMemberModalVisible(false)}
        footer={null}
      >
        <div
          style={{
            marginBottom: "20px",
            display: "flex",
            justifyContent: "space-between",
          }}
        >
          <Radio.Group onChange={handleMemberType} value={memberType}>
            <Radio value="active">Active members</Radio>
            <Radio value="inactive">Inactive members</Radio>
          </Radio.Group>
          {memberType === "active" ? (
            retentionData.activeCohortMembers.length > 0 ? (
              <ExportMenu
                members={retentionData.activeCohortMembers as DiscordMembers[]}
              />
            ) : null
          ) : null}
          {memberType === "inactive" ? (
            retentionData.inactiveCohortMembers.length > 0 ? (
              <ExportMenu
                members={
                  retentionData.inactiveCohortMembers as DiscordMembers[]
                }
              />
            ) : null
          ) : null}
        </div>
        <div
          style={{
            marginBottom: "20px",
            display: "flex",
            justifyContent: "flex-start",
          }}
        >
          <p>
            {memberType === "active" ? (
              <>
                {"Active members who joined on "}
                <span style={{ fontWeight: "bold" }}>
                  {cohortDates.cohortDate}
                </span>
                {" and are active after "}
                <span style={{ fontWeight: "bold" }}>
                  {cohortDates.currentMonth}
                </span>
                {cohortDates.currentMonth === 1 ? " month" : " months"}
              </>
            ) : (
              <>
                {"Inactive members who joined on "}
                <span style={{ fontWeight: "bold" }}>
                  {cohortDates.cohortDate}
                </span>
                {" and are inactive after "}
                <span style={{ fontWeight: "bold" }}>
                  {cohortDates.currentMonth}
                </span>
                {cohortDates.currentMonth === 1 ? " month" : " months"}
              </>
            )}
          </p>
        </div>
        <Spin spinning={retentionData.isLoading}>
          {retentionData.activeCohortMembersError ? (
            <div>
              {/* The inactive cohort members list is generated from the active list, so if the active list
              cannot be fetched than neither inactive nor active can be displayed */}
              <p>
                There was an error loading the active cohort members. Please try
                again.
              </p>
              <code>{retentionData.activeCohortMembersError?.message}</code>
            </div>
          ) : (
            <>
              {memberType === "active" && !retentionData.isLoading && (
                <Table<DiscordMembers>
                  size="small"
                  columns={columns}
                  dataSource={retentionData.activeCohortMembers}
                  rowKey="discord_id"
                  pagination={{
                    defaultPageSize: 50,
                    showSizeChanger: true,
                    pageSizeOptions: ["50", "100", "200"],
                  }}
                />
              )}
              {memberType === "inactive" && !retentionData.isLoading && (
                <Table<DiscordMembers>
                  size="small"
                  columns={columns}
                  dataSource={retentionData.inactiveCohortMembers}
                  rowKey="discord_id"
                  pagination={{
                    defaultPageSize: 50,
                    showSizeChanger: true,
                    pageSizeOptions: ["50", "100", "200"],
                  }}
                />
              )}
            </>
          )}
        </Spin>
      </Modal>
      <Modal
        width={600}
        title="New cohort members"
        style={{ top: 40, right: 10, borderRadius: 20 }}
        visible={isNewMemberModalVisible}
        onCancel={() => setIsNewMemberModalVisible(false)}
        footer={null}
      >
        <Spin spinning={retentionData.isLoading}>
          <div
            style={{
              marginBottom: "10px",
              display: "flex",
              justifyContent: "space-between",
            }}
          >
            <p>
              {"New members who joined on "}
              <span style={{ fontWeight: "bold" }}>
                {cohortDates.cohortDate}
              </span>
            </p>
            {retentionData.newCohortMembers.length > 0 ? (
              <ExportMenu
                members={retentionData.newCohortMembers as DiscordMembers[]}
              />
            ) : null}
          </div>
          {!retentionData.isLoading &&
            (retentionData.newCohortMembersError ? (
              <div>
                <p>Error fetching new cohort members </p>
                <p>
                  <code>{retentionData.newCohortMembersError.message}</code>
                </p>
              </div>
            ) : (
              <Table<DiscordMembers>
                size="small"
                columns={columns}
                dataSource={retentionData.newCohortMembers}
                rowKey="discord_id"
                pagination={{
                  defaultPageSize: 50,
                  showSizeChanger: true,
                  pageSizeOptions: ["50", "100", "200"],
                }}
              />
            ))}
        </Spin>
      </Modal>
    </div>
  );
};

export default RetentionChart;
